import { UnassignDriverPriceResponseDto, UnassignedDriverView } from "@dtos/transports/transport-unassign-driver.dto";
import { AdjustmentOrigin, AdjustmentType } from "@enums/additional.enum";
import { AdjustmentReason, AdjustmentSubType } from "@enums/adjustment";
import { UserRole } from "@enums/user.enum";

export class DriverAdjustment {
    id: number;
    name: string;
    last_name: string;
    email: string;
    tags: number[];
    rating: number;
    image: string | null;

    constructor(data: any = {}) {
        this.id = data.id || 0;
        this.name = data.name || '';
        this.last_name = data.last_name || '';
        this.email = data.email || '';
        this.tags = data.tags || [];
        this.rating = data.rating || 0;
        this.image = data.image || null;
    }

    static fromJson(json: any): DriverAdjustment {
        return new DriverAdjustment(json);
    }

    toJson(): any {
        return {
            id: this.id,
            name: this.name,
            last_name: this.last_name,
            email: this.email,
            tags: this.tags,
            rating: this.rating,
            image: this.image
        };
    }

    copyWith(updates: Partial<DriverAdjustment>): DriverAdjustment {
        return new DriverAdjustment({
            ...this.toJson(),
            ...updates
        });
    }
}

export class UnassignmentCreatorAdjustment {
    id: number;
    name: string;
    last_name: string;
    email: string;
    role: UserRole;

    constructor(data: any = {}) {
        this.id = data.id || 0;
        this.name = data.name || '';
        this.last_name = data.last_name || '';
        this.email = data.email || '';
        this.role = data.role || UserRole.USER;
    }

    static fromJson(json: any): UnassignmentCreatorAdjustment {
        return new UnassignmentCreatorAdjustment(json);
    }

    toJson(): any {
        return {
            id: this.id,
            name: this.name,
            last_name: this.last_name,
            email: this.email,
            role: this.role
        };
    }

    copyWith(updates: Partial<UnassignmentCreatorAdjustment>): UnassignmentCreatorAdjustment {
        return new UnassignmentCreatorAdjustment({
            ...this.toJson(),
            ...updates
        });
    }
}



export class Adjustment {
    id: number;
    driver: DriverAdjustment | null;
    transport: number | null;
    incidence: number | null;
    invoice_concept: number | null;
    adjustment_type: AdjustmentType;
    reason: AdjustmentReason;
    reason_sub_type: AdjustmentSubType | null;
    message: string;
    amount: number;
    percentage: number | null;
    origin: AdjustmentOrigin;
    signed_amount: number;
    is_invoiced: boolean;
    has_transport: boolean;
    created_date: string;
    quantity: number;
    manual: boolean;
    unassignment_creator: UnassignmentCreatorAdjustment | null;
    unassigment?: UnassignedDriverView

    constructor(data: any = {}) {
        this.id = data.id || 0;
        this.driver = data.driver ? new DriverAdjustment(data.driver) : null;
        this.transport = data.transport !== undefined ? data.transport : null;
        this.incidence = data.incidence !== undefined ? data.incidence : null;
        this.invoice_concept = data.invoice_concept ? Number(data.invoice_concept) : null;
        this.adjustment_type = data.adjustment_type || AdjustmentType.PENALTY;
        this.reason = data.reason || AdjustmentReason.OTHER;
        this.reason_sub_type = data.reason_sub_type || null;
        this.message = data.message || '';
        this.amount = data.amount || 0;
        this.percentage = data.percentage || null;
        this.origin = data.origin || AdjustmentOrigin.OTHER;
        this.signed_amount = data.signed_amount || 0;
        this.is_invoiced = data.is_invoiced || false;
        this.has_transport = data.has_transport || false;
        this.created_date = data.created_date || '';
        this.quantity = data.quantity || 0;
        this.manual = data.manual ?? false;
        this.unassignment_creator = data.unassignment_creator ? new UnassignmentCreatorAdjustment(data.unassignment_creator) : null;
        if(data.unassignment){
            this.unassigment = UnassignedDriverView.fromJson(data.unassignment);
        }
    }

    static fromJson(json: any): Adjustment {
        return new Adjustment(json);
    }

    toJson(): any {
        return {
            id: this.id,
            driver: this.driver?.toJson() || null,
            transport: this.transport,
            incidence: this.incidence,
            invoice_concept: this.invoice_concept,
            adjustment_type: this.adjustment_type,
            reason: this.reason,
            reason_sub_type: this.reason_sub_type,
            message: this.message,
            amount: this.amount,
            percentage: this.percentage,
            origin: this.origin,
            signed_amount: this.signed_amount,
            is_invoiced: this.is_invoiced,
            has_transport: this.has_transport,
            created_date: this.created_date,
            quantity: this.quantity,
            unassignment_creator: this.unassignment_creator?.toJson() || null
        };
    }

    copyWith(updates: Partial<Adjustment>): Adjustment {
        return new Adjustment({
            ...this.toJson(),
            ...updates
        });
    }
}














export class CreateAdjustmentDto {
    driver: number = 0;
    adjustment_type: AdjustmentType = AdjustmentType.PENALTY;
    reason: AdjustmentReason = AdjustmentReason.OTHER;
    transport: number | null = null;
    incidence: number | null = null;
    reason_sub_type: AdjustmentSubType | null = null;
    message: string = '';
    quantity: number = 0;
    manual: boolean = false;
    amount: number = 0;

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    static fromJson(json: any): CreateAdjustmentDto {
        return new CreateAdjustmentDto(json);
    }

    toJson(): any {
        return {
            driver: this.driver,
            adjustment_type: this.adjustment_type,
            reason: this.reason,
            transport: this.transport,
            incidence: this.incidence,
            reason_sub_type: this.reason_sub_type,
            message: this.message,
            quantity: this.quantity,
            manual: this.manual,
            amount: this.amount
        };
    }

    copyFrom(data: any): void {
        if (!data) return;
        if (data.driver !== undefined) this.driver = data.driver;
        if (data.adjustment_type !== undefined) this.adjustment_type = data.adjustment_type;
        if (data.reason !== undefined) this.reason = data.reason;
        if (data.transport !== undefined) this.transport = data.transport;
        if (data.incidence !== undefined) this.incidence = data.incidence;
        if (data.reason_sub_type !== undefined) this.reason_sub_type = data.reason_sub_type;
        if (data.message !== undefined) this.message = data.message;
        if (data.quantity !== undefined) this.quantity = data.quantity;
        if (data.manual !== undefined) this.manual = data.manual;
        if (data.amount !== undefined) this.amount = data.amount;
    }
}

export interface AdjustmentsListRequest {
    /**
     * Tipo de regularización, * `PENALTY` - Penalización
     * * `BONUS` - Bono
     */
    adjustment_type?: AdjustmentType;
    driver?: number;
    invoice_concept?: number;
    is_invoiced?: string;
    /**
     * Origen de la regularización, * `UNASSIGNATION` - Desasignación
     * * `INCIDENCE` - Incidencia
     * * `TRANSPORT` - Transporte
     * * `OTHER` - Otro
     */
    origin?: AdjustmentOrigin;
    /**
     * A page number within the paginated result set.
     */
    page?: number;
    /**
     * Number of results to return per page.
     */
    page_size?: number;
    transport?: number;
    [property: string]: any;
}

export class AdjustmentIncidenceResponseItem{
    id: number = 0;
    adjustment_type: AdjustmentType = AdjustmentType.PENALTY;
    message: string = "";
    amount: number = 0.0;
    quantity?: number = 0;
}

